import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/layouts/PageContainer";
import {
  ClipboardCheck,
  ChevronLeft,
  AlertCircle,
  Check,
  X,
  BookOpen,
} from "lucide-react";

export default function ManageClassSharing() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [school, setSchool] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, reqRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/share-requests`, { headers }),
      ]);
      if (!reqRes.ok) {
        const body = await reqRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load share requests");
      }
      if (schoolRes.ok) setSchool(await schoolRes.json());
      setRequests(await reqRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAction = async (requestId, action) => {
    setActionInProgress(requestId + action);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/share-requests/${requestId}/${action}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Failed to ${action} request`);
      }
      setRequests((prev) => prev.filter((r) => r._id !== requestId));
      toast.success(action === "approve" ? "Request approved." : "Request denied.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <ClipboardCheck size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">
            Share Requests
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {requests.length} pending request{requests.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <ClipboardCheck size={32} className="text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No pending requests</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            When teachers request to share a class, they will appear here for your
            approval.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const busy = actionInProgress?.startsWith(req._id);
            return (
              <div
                key={req._id}
                className="bg-card border border-border rounded-xl px-5 py-4"
              >
                {/* Class info */}
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen size={14} className="text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium text-foreground">
                    {req.reportGroupId?.name ||
                      [
                        req.reportGroupId?.grade &&
                          `Grade ${req.reportGroupId.grade}`,
                        req.reportGroupId?.schoolYear,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                  </span>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full ml-auto">
                    {req.permission} access
                  </span>
                </div>

                {/* Requester → Target */}
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {req.requesterId?.name || "A teacher"}
                  </span>{" "}
                  wants to share with{" "}
                  <span className="font-medium text-foreground">
                    {req.targetUserId?.name || "another teacher"}
                  </span>{" "}
                  ({req.targetUserId?.email})
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    className="gap-1.5 h-8"
                    disabled={busy}
                    onClick={() => handleAction(req._id, "approve")}
                  >
                    <Check size={13} />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 h-8 text-muted-foreground hover:text-destructive hover:border-destructive/40"
                    disabled={busy}
                    onClick={() => handleAction(req._id, "deny")}
                  >
                    <X size={13} />
                    Deny
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
